import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  BLOCK_MESSAGES,
  SUPPORT_STATUS_LABELS,
  type AdminCompany,
  type AdminMailSettings,
  type AdminMicrosoftSettings,
  type AdminStats,
  type EmailBlockCode,
} from "@handvero/shared";
import { api, errorText } from "../api";
import { BlogAdmin } from "./AdminBlog";
import { DHead } from "./Desktop";
import { sizeText } from "./MobileApp";
import { Icon, Message } from "./ui";

/** Bereich nur für HandVero selbst: Zahlen, Firmen mit Support-Zugang und der Mailserver. */

const USAGE_LABELS: Record<string, string> = {
  login: "Anmeldungen",
  company_verified: "Neue Firmen bestätigt",
  project_created: "Projekte angelegt",
  file_uploaded: "Dateien hochgeladen",
  note_created: "Notizen geschrieben",
  voice_note_created: "Sprachmemos aufgenommen",
  event_created: "Termine eingetragen",
  device_linked: "Handys verknüpft",
  support_opened: "Support-Sitzungen",
};

const fmt = (n: number) => n.toLocaleString("de-DE");
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "–");
const time = (iso: string) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

interface ContactRequestRow {
  id: string;
  name: string;
  email: string;
  companyName: string | null;
  message: string;
  context: string | null;
  source: string;
  mailSent: boolean;
  createdAt: string;
}

function ContactRequests() {
  const [rows, setRows] = useState<ContactRequestRow[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get<ContactRequestRow[]>("/api/admin/contact-requests").then(setRows, (e) => setError(errorText(e)));
  }, []);
  if (error) return <Message>{error}</Message>;
  if (!rows) return <p className="muted-s">Lädt …</p>;
  if (rows.length === 0) return <p className="muted-s">Noch keine Kundenanfragen.</p>;
  return (
    <div className="adm">
      {rows.map((r) => (
        <article key={r.id} style={{ borderBottom: "1px solid var(--line, #d8dde6)", paddingBottom: 18 }}>
          <h3>
            {r.name}
            {r.companyName ? `, ${r.companyName}` : ""}
          </h3>
          <p>
            <a href={`mailto:${r.email}`}>{r.email}</a> · {time(r.createdAt)} · {r.source === "vero" ? "über Vero" : "Website"}
            {r.mailSent ? "" : " · E-Mail an Postfach nicht angekommen"}
          </p>
          <p style={{ whiteSpace: "pre-wrap" }}>{r.message}</p>
          {r.context && <p className="muted-s">Frage an Vero: {r.context}</p>}
        </article>
      ))}
    </div>
  );
}

export function AdminPage() {
  const { pathname } = useLocation();
  const tab = /\/admin\/(firmen|anfragen|berater|mail|outlook|blog)/.exec(pathname)?.[1] ?? "uebersicht";
  const tabs: [string, string, string][] = [
    ["uebersicht", "Übersicht", "/desktop/admin"],
    ["firmen", "Firmen und Support", "/desktop/admin/firmen"],
    ["anfragen", "Kundenanfragen", "/desktop/admin/anfragen"],
    ["berater", "Berater", "/desktop/admin/berater"],
    ["mail", "E-Mail-Versand", "/desktop/admin/mail"],
    ["outlook", "Outlook", "/desktop/admin/outlook"],
    ["blog", "Blog", "/desktop/admin/blog"],
  ];
  return (
    <section className="dproject dsimple">
      <DHead kicker="Nur für HandVero sichtbar" title="Admin">
        <nav className="dsegs" aria-label="Bereiche des Admin-Bereichs">
          {tabs.map(([id, label, to]) => (
            <Link key={id} to={to} className={tab === id ? "on" : undefined} aria-current={tab === id ? "page" : undefined} replace>
              {label}
            </Link>
          ))}
        </nav>
      </DHead>
      {tab === "uebersicht" && <Overview />}
      {tab === "firmen" && <Companies />}
      {tab === "anfragen" && <ContactRequests />}
      {tab === "berater" && <Advisor />}
      {tab === "mail" && <MailSettings />}
      {tab === "outlook" && <MicrosoftSettings />}
      {tab === "blog" && <BlogAdmin />}
    </section>
  );
}

// ---------- Übersicht ----------

function Overview() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get<AdminStats>("/api/admin/stats").then(setStats, (err) => setError(errorText(err)));
  }, []);
  if (error) return <Message>{error}</Message>;
  if (!stats) return <div className="skel-block" />;

  const figures: [string, string, string][] = [
    [fmt(stats.companies.active), "Firmen aktiv", `${fmt(stats.companies.pending)} warten auf Bestätigung · ${fmt(stats.companies.blocked)} gesperrt`],
    [`${stats.founders.assigned}/${stats.founders.total}`, "Gründerplätze", `${stats.founders.total - stats.founders.assigned} noch frei`],
    [fmt(stats.activeCompanies7), "Aktiv in 7 Tagen", `${fmt(stats.activeCompanies30)} in 30 Tagen`],
    [fmt(stats.users), "Zugänge", "Inhaber, Admins und Mitarbeiter"],
    [fmt(stats.projects), "Projekte", `${fmt(stats.files)} Dateien`],
    [sizeText(stats.storageBytes), "Speicher", "Fotos und Dokumente"],
  ];
  const max = Math.max(1, ...stats.days.map((d) => d.registered + d.blocked));
  return (
    <div className="adm">
      <ol className="adm-figures">
        {figures.map(([value, label, sub], i) => (
          <li key={label} style={{ animationDelay: `${i * 60}ms` }}>
            <span className="adm-num">{String(i + 1).padStart(2, "0")}</span>
            <b>{value}</b>
            <span className="adm-label">{label}</span>
            <small>{sub}</small>
          </li>
        ))}
      </ol>

      <section className="adm-panel">
        <div className="kick-line">
          <span>30</span>
          <span>▼ Registrierungen der letzten 30 Tage</span>
        </div>
        <div className="adm-chart" role="img" aria-label="Registrierungen pro Tag">
          {stats.days.map((d) => (
            <div key={d.day} className="adm-bar" title={`${date(d.day)}: ${d.registered} angenommen, ${d.verified} bestätigt, ${d.blocked} blockiert`}>
              <span className="bad" style={{ height: `${(d.blocked / max) * 100}%` }} />
              <span className="ok" style={{ height: `${(d.registered / max) * 100}%` }} />
            </div>
          ))}
        </div>
        <p className="adm-legend">
          <span className="ok" /> angenommen <span className="bad" /> blockiert
        </p>
      </section>

      <div className="adm-two">
        <section className="adm-panel">
          <div className="kick-line">
            <span>{String(stats.blockReasons.reduce((a, r) => a + r.count, 0)).padStart(2, "0")}</span>
            <span>▼ Warum blockiert</span>
          </div>
          {stats.blockReasons.length === 0 && <p className="muted-s">In den letzten 30 Tagen wurde niemand blockiert.</p>}
          <ul className="adm-list">
            {stats.blockReasons.map((r) => (
              <li key={r.reason}>
                <span>{BLOCK_MESSAGES[r.reason as EmailBlockCode]?.split(".")[0] ?? r.reason}</span>
                <b>{fmt(r.count)}</b>
              </li>
            ))}
          </ul>
        </section>
        <section className="adm-panel">
          <div className="kick-line">
            <span>{String(stats.usage.length).padStart(2, "0")}</span>
            <span>▼ Nutzung in 30 Tagen</span>
          </div>
          {stats.usage.length === 0 && <p className="muted-s">Noch keine Nutzung.</p>}
          <ul className="adm-list">
            {stats.usage.map((u) => (
              <li key={u.kind}>
                <span>{USAGE_LABELS[u.kind] ?? u.kind}</span>
                <b>{fmt(u.count)}</b>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

// ---------- Firmen und Support ----------

function Companies() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [list, setList] = useState<AdminCompany[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api.get<AdminCompany[]>(`/api/admin/companies?q=${encodeURIComponent(q)}`).then(setList, (err) => setMsg(errorText(err)));
  }, [q]);
  useEffect(() => {
    const id = window.setTimeout(load, 250);
    return () => window.clearTimeout(id);
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setMsg("");
    try {
      await fn();
      load();
    } catch (err) {
      setMsg(errorText(err));
    }
  }

  async function ask(e: FormEvent, c: AdminCompany) {
    e.preventDefault();
    await act(() => api.post(`/api/admin/companies/${c.id}/support`, { reason }));
    setReason("");
  }

  async function enter(grantId: string) {
    setMsg("");
    try {
      await api.post(`/api/admin/support/${grantId}/open`);
      navigate("/desktop", { replace: true });
      window.location.reload();
    } catch (err) {
      setMsg(errorText(err));
    }
  }

  return (
    <div className="adm">
      <input className="adm-search" type="search" placeholder="Firma, Inhaber oder E-Mail suchen" value={q} onChange={(e) => setQ(e.target.value)} />
      <Message>{msg}</Message>
      {list && list.length === 0 && <p className="muted-s">Keine Firma gefunden.</p>}
      <ol className="adm-companies">
        {list?.map((c, i) => {
          const s = c.support;
          const isOpen = open === c.id;
          return (
            <li key={c.id} className={isOpen ? "open" : undefined}>
              <button className="adm-row" onClick={() => setOpen(isOpen ? null : c.id)} aria-expanded={isOpen}>
                <span className="adm-num">{String(i + 1).padStart(2, "0")}</span>
                <span className="adm-name">
                  <b>{c.name}</b>
                  <small>
                    {c.ownerName ?? "ohne Inhaber"} · {c.ownerEmail ?? "–"}
                  </small>
                </span>
                <span className="adm-meta">
                  <small>{c.planLabel}{c.founderNumber ? ` Nr. ${c.founderNumber}` : ""}</small>
                  <small>
                    {c.users} Zugänge · {c.projects} Projekte · {sizeText(c.storageBytes)}
                  </small>
                </span>
                <span className="adm-pills">
                  {s?.active && <span className="adm-pill support">Support frei</span>}
                  <span className={`adm-pill ${c.status}`}>{c.status === "active" ? "aktiv" : c.status === "pending" ? "wartet" : "gesperrt"}</span>
                </span>
              </button>
              {isOpen && (
                <div className="adm-detail">
                  <dl>
                    <dt>Registriert</dt>
                    <dd>{date(c.createdAt)}</dd>
                    <dt>Zuletzt aktiv</dt>
                    <dd>{date(c.lastActivity)}</dd>
                    <dt>Branche</dt>
                    <dd>{c.trade ?? "–"}</dd>
                    <dt>Dateien</dt>
                    <dd>{fmt(c.files)}</dd>
                  </dl>
                  <div className="adm-support">
                    <h3>Support-Zugang</h3>
                    {s && (
                      <p className="muted-s">
                        {SUPPORT_STATUS_LABELS[s.status]}
                        {s.active && s.expiresAt ? ` bis ${time(s.expiresAt)}` : ""} · „{s.reason}“
                      </p>
                    )}
                    {s?.active ? (
                      <button className="dbtn" onClick={() => void enter(s.id)}>
                        Konto öffnen
                      </button>
                    ) : s?.status === "requested" ? (
                      <p className="muted-s">Wartet auf die Zustimmung der Firma. Sie hat eine E-Mail bekommen.</p>
                    ) : (
                      <form className="adm-ask" onSubmit={(e) => void ask(e, c)}>
                        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Wobei soll der Support helfen?" maxLength={500} required minLength={5} />
                        <button className="dbtn">Zugang anfragen</button>
                      </form>
                    )}
                    <p className="muted-s">
                      Die Firma entscheidet selbst, ob und wie lange. Ohne ihre Zustimmung öffnet sich nichts, und sie kann den Zugang jederzeit beenden.
                    </p>
                  </div>
                  <div className="adm-actions">
                    <button
                      className="dbtn ghost"
                      onClick={() => void act(() => api.post(`/api/admin/companies/${c.id}/addons`, { plans: !c.addonPlans }))}
                    >
                      {c.addonPlans ? "Zeichenprogramm ausschalten" : "Zeichenprogramm freischalten (Zusatzabo 1)"}
                    </button>
                    <button
                      className="dbtn ghost"
                      onClick={() => void act(() => api.post(`/api/admin/companies/${c.id}/addons`, { billing: !c.addonBilling }))}
                    >
                      {c.addonBilling ? "Angebote und Rechnungen ausschalten" : "Angebote und Rechnungen freischalten"}
                    </button>
                    {c.status === "blocked" ? (
                      <button className="dbtn ghost" onClick={() => void act(() => api.post(`/api/admin/companies/${c.id}/status`, { status: "active" }))}>
                        Entsperren
                      </button>
                    ) : (
                      <button
                        className="dbtn ghost danger"
                        onClick={() =>
                          window.confirm(`${c.name} sperren? Alle Zugänge werden sofort abgemeldet.`) &&
                          void act(() => api.post(`/api/admin/companies/${c.id}/status`, { status: "blocked" }))
                        }
                      >
                        Firma sperren
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---------- E-Mail-Versand ----------

function MailSettings() {
  const [s, setS] = useState<AdminMailSettings | null>(null);
  const [mode, setMode] = useState<AdminMailSettings["mode"]>("microsoft");
  const [pass, setPass] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<AdminMailSettings>("/api/admin/mail").then(
      (x) => {
        setS(x);
        setMode(x.mode);
      },
      (err) => setMsg({ ok: false, text: errorText(err) }),
    );
  }, []);
  useEffect(load, [load]);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  function save(e: FormEvent) {
    e.preventDefault();
    if (!s) return;
    void run(async () => {
      if (mode === "microsoft") {
        await api.put("/api/admin/mail/microsoft", { from: s.from });
      } else {
        await api.put("/api/admin/mail", { host: s.host, port: s.port, secure: s.secure, user: s.user, pass: pass || undefined, from: s.from });
        setPass("");
      }
      return "Gespeichert. Am besten gleich eine Testmail schicken.";
    });
  }

  if (!s) return msg ? <Message>{msg.text}</Message> : <div className="skel-block" />;
  const source = { admin: "Eingerichtet", umgebung: "Aus der Server-Einrichtung", keine: "Noch kein Mailversand" }[s.source];
  const saved = s.source !== "keine" && s.mode === mode;
  return (
    <div className="adm adm-mail">
      <form className="app-form" onSubmit={save}>
        <p className="adm-source">
          <span className={`adm-pill ${s.source === "keine" ? "blocked" : "active"}`}>{source}</span>
          Hierüber gehen Bestätigungen, Einladungen und Support-Anfragen raus.
        </p>
        <div className="access-modes" role="radiogroup" aria-label="Versandweg">
          <button type="button" role="radio" aria-checked={mode === "microsoft"} className={mode === "microsoft" ? "on" : undefined} onClick={() => setMode("microsoft")}>
            <Icon name="shield" />
            <span>
              <b>Über Microsoft 365 (empfohlen)</b>
              <small>Ohne Passwort. HandVero sendet mit einer eigenen, eingeschränkten Erlaubnis von Microsoft.</small>
            </span>
          </button>
          <button type="button" role="radio" aria-checked={mode === "smtp"} className={mode === "smtp" ? "on" : undefined} onClick={() => setMode("smtp")}>
            <Icon name="send" />
            <span>
              <b>Eigener Mailserver</b>
              <small>Mit Servername, Benutzer und Passwort eines Postfachs.</small>
            </span>
          </button>
        </div>

        {mode === "microsoft" ? (
          <>
            <ol className="adm-steps">
              <li>
                Im <b>Microsoft 365 Admin Center</b> (admin.microsoft.com) unter „Teams &amp; Gruppen“ → „Freigegebene Postfächer“ ein Postfach anlegen, z. B.
                service@handvero.de. Freigegebene Postfächer sind kostenlos und brauchen kein Passwort.
              </li>
              <li>
                Im Tab <b>„Outlook“</b> die App-Registrierung eintragen.{" "}
                {s.microsoftReady ? <span className="adm-ok-inline">Erledigt.</span> : <span className="adm-bad-inline">Fehlt noch.</span>}
              </li>
              <li>
                Im <b>Azure-Portal</b> bei der App unter „API-Berechtigungen“ → „Berechtigung hinzufügen“ → Microsoft Graph →{" "}
                <b>Anwendungsberechtigungen</b> → <b>Mail.Send</b> wählen. Danach auf „Administratorzustimmung für … erteilen“ klicken.
              </li>
              <li>Unten die Absenderadresse eintragen, speichern und eine Testmail schicken.</li>
            </ol>
            <label>
              Absender
              <input value={s.from} onChange={(e) => setS({ ...s, from: e.target.value })} placeholder="HandVero <service@handvero.de>" required />
            </label>
          </>
        ) : (
          <>
            <div className="two">
              <label>
                Mailserver
                <input value={s.host} onChange={(e) => setS({ ...s, host: e.target.value })} placeholder="smtp.example.de" required />
              </label>
              <label>
                Port
                <input type="number" value={s.port} onChange={(e) => setS({ ...s, port: Number(e.target.value) })} min={1} max={65535} required />
              </label>
            </div>
            <label className="check">
              <input type="checkbox" checked={s.secure} onChange={(e) => setS({ ...s, secure: e.target.checked })} />
              Direkt verschlüsselt verbinden (Port 465). Bei Port 587 aus lassen.
            </label>
            <div className="two">
              <label>
                Benutzer
                <input value={s.user} onChange={(e) => setS({ ...s, user: e.target.value })} placeholder="service@handvero.de" autoComplete="off" />
              </label>
              <label>
                Passwort
                <input
                  type="password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder={s.hasPassword ? "gespeichert, leer lassen zum Behalten" : ""}
                  autoComplete="new-password"
                />
              </label>
            </div>
            <label>
              Absender
              <input value={s.from} onChange={(e) => setS({ ...s, from: e.target.value })} placeholder="HandVero <service@handvero.de>" required />
            </label>
          </>
        )}
        {msg && <p className={msg.ok ? "adm-ok" : "adm-bad"}>{msg.text}</p>}
        <div className="adm-actions">
          <button className="dbtn" disabled={busy}>
            Speichern
          </button>
          <button
            type="button"
            className="dbtn ghost"
            disabled={busy || !saved}
            title={saved ? undefined : "Erst speichern, dann testen"}
            onClick={() => void run(async () => `Testmail an ${(await api.post<{ to: string }>("/api/admin/mail/test")).to} verschickt.`)}
          >
            Testmail schicken
          </button>
          {s.source === "admin" && (
            <button
              type="button"
              className="dbtn ghost danger"
              disabled={busy}
              onClick={() =>
                window.confirm("Eingetragenen Mailversand entfernen?") &&
                void run(async () => {
                  await api.del("/api/admin/mail");
                  return "Entfernt.";
                })
              }
            >
              Entfernen
            </button>
          )}
        </div>
        <p className="muted-s">
          {mode === "microsoft"
            ? "HandVero bekommt kein Passwort. Die Erlaubnis zum Senden lässt sich im Azure-Portal jederzeit wieder entziehen."
            : "Das Passwort liegt verschlüsselt in der Datenbank und wird nie wieder angezeigt."}
        </p>
      </form>
    </div>
  );
}

// ---------- Outlook ----------

function MicrosoftSettings() {
  const [s, setS] = useState<AdminMicrosoftSettings | null>(null);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<AdminMicrosoftSettings>("/api/admin/microsoft").then((x) => {
      setS(x);
      setClientId(x.clientId);
    }, (err) => setMsg({ ok: false, text: errorText(err) }));
  }, []);
  useEffect(load, [load]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.put("/api/admin/microsoft", { clientId, clientSecret: secret || undefined });
      setSecret("");
      setMsg({ ok: true, text: "Gespeichert. Ab jetzt sehen alle im Kalender den Knopf „Mit Outlook verbinden“." });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  if (!s) return msg ? <Message>{msg.text}</Message> : <div className="skel-block" />;
  const source = { admin: "Eingerichtet", umgebung: "Aus der Server-Einrichtung", keine: "Noch nicht eingerichtet" }[s.source];
  return (
    <div className="adm adm-mail">
      <p className="adm-source">
        <span className={`adm-pill ${s.source === "keine" ? "blocked" : "active"}`}>{source}</span>
        {s.connections} {s.connections === 1 ? "Person hat" : "Personen haben"} Outlook verbunden.
      </p>
      <ol className="adm-steps">
        <li>
          Im <b>Azure-Portal</b> (portal.azure.com) unter „App-Registrierungen“ auf „Neue Registrierung“. Name: HandVero. Kontotypen: „Konten in
          einem beliebigen Organisationsverzeichnis und persönliche Microsoft-Konten“.
        </li>
        <li>
          Als Umleitungs-URI „Web“ wählen und diese Adresse eintragen:
          <code className="adm-code">{s.redirectUri}</code>
          <button type="button" className="dbtn ghost small" onClick={() => void navigator.clipboard?.writeText(s.redirectUri)}>
            Kopieren
          </button>
        </li>
        <li>
          Unter „API-Berechtigungen“ für Microsoft Graph (delegiert) hinzufügen: Calendars.ReadWrite, User.Read, offline_access. Für den Mailversand ohne
          Passwort zusätzlich Mail.Send als Anwendungsberechtigung, dann „Administratorzustimmung erteilen“ (siehe Tab „E-Mail-Versand“).
        </li>
        <li>Unter „Zertifikate &amp; Geheimnisse“ einen neuen geheimen Clientschlüssel anlegen und den Wert kopieren. Er läuft nach höchstens 24 Monaten ab.</li>
        <li>Anwendungs-ID (Client-ID) von der Übersichtsseite und den Schlüssel hier eintragen.</li>
      </ol>
      <form className="app-form" onSubmit={(e) => void save(e)}>
        <div className="two">
          <label>
            Anwendungs-ID (Client-ID)
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="1a2b3c4d-…" required autoComplete="off" />
          </label>
          <label>
            Geheimer Schlüssel
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={s.hasSecret ? "gespeichert, leer lassen zum Behalten" : ""}
              autoComplete="new-password"
            />
          </label>
        </div>
        {msg && <p className={msg.ok ? "adm-ok" : "adm-bad"}>{msg.text}</p>}
        <div className="adm-actions">
          <button className="dbtn" disabled={busy}>
            Speichern
          </button>
        </div>
        <p className="muted-s">Der Schlüssel liegt verschlüsselt in der Datenbank. Die Zugänge der Kunden zu ihrem Outlook ebenso.</p>
      </form>
    </div>
  );
}


// ---------- Berater ----------

interface AdvisorItem {
  key: string;
  area: string;
  severity: "hoch" | "mittel" | "niedrig";
  title: string;
  evidence: string;
  impact: string;
  task: string;
  fromData: boolean;
  status: "open" | "handed_over" | "done" | "dismissed";
}

function Advisor() {
  const [items, setItems] = useState<AdvisorItem[] | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const load = useCallback(() => api.get<{ findings: AdvisorItem[] }>("/api/admin/advisor").then((r) => setItems(r.findings), (e) => setError(errorText(e))), []);
  useEffect(() => {
    void load();
  }, [load]);
  if (error) return <Message>{error}</Message>;
  if (!items) return <div className="skel-block" />;

  async function setStatus(key: string, status: AdvisorItem["status"]) {
    await api.post(`/api/admin/advisor/${key}`, { status }).catch((e) => setError(errorText(e)));
    await load();
  }
  async function implement(it: AdvisorItem) {
    const text = `Bitte umsetzen (Berater, ${it.area}): ${it.title}\n\nWarum: ${it.impact}\nAuftrag: ${it.task}\nHinweis: erst auf der Vorschau, nichts Bestehendes ändern.`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(it.key);
    } catch {
      window.prompt("Diesen Text in den Chat kopieren:", text);
    }
    await setStatus(it.key, "handed_over");
  }
  const open = items.filter((i) => i.status === "open" || i.status === "handed_over");
  const closed = items.filter((i) => i.status === "done" || i.status === "dismissed");
  return (
    <div className="adm stack">
      <p className="muted-s">
        Der Berater prüft HandVero kritisch aus Sicht von Produkt, Design, Kundenberatung und Markt. Ziel ist eine reale Wirkung bei Kunden, nicht eine lange Liste.
        „Aus Zahlen“ heißt: aus Ihren echten Daten abgeleitet. „Einschätzung“ ist eine begründete Meinung. Er arbeitet ohne externe KI-Dienste, die Regeln liegen im Programm.
        „Umsetzen“ kopiert einen fertigen Auftrag, den Sie in den Entwickler-Chat einfügen.
      </p>
      {open.length === 0 && <Message>Aktuell ist nichts offen.</Message>}
      {open.map((it) => (
        <article key={it.key} className="card stack" style={{ borderLeft: `4px solid ${it.severity === "hoch" ? "#C0392B" : it.severity === "mittel" ? "#D68910" : "#7F8C8D"}` }}>
          <div>
            <span className="tag">{it.area}</span> <span className="tag">{it.severity === "hoch" ? "Wichtig" : it.severity === "mittel" ? "Mittel" : "Später"}</span>{" "}
            <span className="muted-s">{it.fromData ? "Aus Zahlen" : "Einschätzung"}</span>
            {it.status === "handed_over" && <span className="muted-s"> · übergeben</span>}
          </div>
          <h3>{it.title}</h3>
          <p>
            <b>Beobachtung:</b> {it.evidence}
          </p>
          <p>
            <b>Marktwirkung:</b> {it.impact}
          </p>
          <p className="muted-s">
            <b>Vorschlag:</b> {it.task}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="dbtn" onClick={() => implement(it)}>
              {copied === it.key ? "Auftrag kopiert" : "Umsetzen"}
            </button>
            <button className="dbtn ghost" onClick={() => setStatus(it.key, "done")}>
              Erledigt
            </button>
            <button className="dbtn ghost" onClick={() => setStatus(it.key, "dismissed")}>
              Nicht sinnvoll
            </button>
          </div>
        </article>
      ))}
      {closed.length > 0 && (
        <p>
          <button className="dbtn" onClick={() => setShowClosed((v) => !v)}>
            {showClosed ? "Erledigte ausblenden" : `Erledigte und verworfene anzeigen (${closed.length})`}
          </button>
        </p>
      )}
      {showClosed &&
        closed.map((it) => (
          <p key={it.key} className="muted-s">
            {it.status === "done" ? "Erledigt" : "Verworfen"}: {it.title}{" "}
            <button className="dbtn" onClick={() => setStatus(it.key, "open")}>
              Wieder öffnen
            </button>
          </p>
        ))}
    </div>
  );
}
